import React, { useEffect, useState } from "react";
import {
  Box, Button, Checkbox, Chip, CircularProgress, Collapse, Dialog, DialogContent, DialogTitle,
  Divider, FormControlLabel, Grid, IconButton, InputAdornment, TextField,
  Tooltip, Typography,
} from "@mui/material";
import { Add, Close, Delete, Edit, Search, LockOutlined, ExpandMore, ExpandLess } from "@mui/icons-material";
import CustomSnackbar from "../components/Common/CustomSnackbar";
import { useSnackbar } from "../components/Common/hooks/useSnackbar";
import useAuth from "../hoc/AuthProvider/useAuth";
import { Permissions } from "../config/permissions";
import { getRoleList, createRole, updateRole, deleteRole, assignPermissions } from "../services/roleService";
import { getPermissionList } from "../services/permissionService";

interface PermissionItem {
  id: number;
  name: string;
  permissionGroup: string;
  displayName: string;
}
interface RoleDto {
  id: number;
  name: string;
  permissions: string[];
}

const Roles: React.FC = () => {
  const { havePermission } = useAuth();
  const { showSuccess, showError, snackbarProps } = useSnackbar();

  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [allPermissions, setAllPermissions] = useState<PermissionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [expandedRoleId, setExpandedRoleId] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [roleName, setRoleName] = useState("");
  const [roleNameError, setRoleNameError] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([getRoleList(), getPermissionList()]);
      setRoles((rolesRes.data as any).data ?? []);
      setAllPermissions((permsRes.data as any).data ?? []);
    } catch (e) {
      showError(e as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const groupedPermissions = allPermissions.reduce<Record<string, PermissionItem[]>>((acc, p) => {
    if (!acc[p.permissionGroup]) acc[p.permissionGroup] = [];
    acc[p.permissionGroup].push(p);
    return acc;
  }, {});

  const openCreate = () => {
    setEditId(null);
    setRoleName("");
    setRoleNameError("");
    setSelectedIds(new Set());
    setDialogOpen(true);
  };

  const openEdit = (role: RoleDto) => {
    setEditId(role.id);
    setRoleName(role.name);
    setRoleNameError("");
    const ids = new Set(allPermissions.filter((p) => role.permissions.includes(p.name)).map((p) => p.id));
    setSelectedIds(ids);
    setDialogOpen(true);
  };

  const handleClose = () => { setDialogOpen(false); setRoleNameError(""); };

  const togglePermission = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleGroup = (group: string) => {
    const groupIds = groupedPermissions[group].map((p) => p.id);
    const allSelected = groupIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      groupIds.forEach((id) => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const handleSave = async () => {
    if (!roleName.trim()) { setRoleNameError("Rol adı boş olamaz."); return; }
    if (roleName.trim().length > 100) { setRoleNameError("Rol adı en fazla 100 karakter olabilir."); return; }
    try {
      const permissionIds = Array.from(selectedIds);
      if (editId !== null) {
        await updateRole({ id: editId, name: roleName });
        await assignPermissions({ roleId: editId, permissionIds });
        showSuccess("Rol güncellendi.");
      } else {
        await createRole({ name: roleName, permissionIds });
        showSuccess("Rol oluşturuldu.");
      }
      setDialogOpen(false);
      await fetchData();
    } catch (e) {
      showError(e as Error);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await deleteRole(id);
      if (!(res.data as any).success) { showError(new Error((res.data as any).message)); return; }
      showSuccess("Rol silindi.");
      await fetchData();
    } catch (e) {
      showError(e as Error);
    }
  };

  const filtered = roles.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <div className="panel-container">
        <Box sx={{ bgcolor: "#fff", p: 2, boxShadow: "0 4px 8px rgba(0,0,0,0.1)", mb: 2 }}>
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid size={{ xs: 12, sm: "auto" }}>
              <TextField
                size="small"
                placeholder="Rol ara"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            {havePermission(Permissions.Role_Create) && (
              <Grid size={{ xs: 12, sm: "auto" }}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={openCreate}
                  sx={{
                    bgcolor: "var(--color-primary)",
                    "&:hover": { bgcolor: "var(--color-primary-dark)" },
                    textTransform: "none",
                    borderRadius: "6px",
                  }}
                >
                  Rol Ekle
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ bgcolor: "#fff", boxShadow: "0 4px 8px rgba(0,0,0,0.1)", borderRadius: 1 }}>
            {filtered.map((role, idx) => (
              <Box
                key={role.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 2,
                  borderBottom: idx < filtered.length - 1 ? "1px solid #f0f0f0" : "none",
                  gap: 2,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography fontWeight={600}>{role.name}</Typography>
                    <Chip
                      label={`${role.permissions.length} yetki`}
                      size="small"
                      icon={<LockOutlined sx={{ fontSize: "13px !important" }} />}
                      deleteIcon={expandedRoleId === role.id ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      onDelete={() => setExpandedRoleId(expandedRoleId === role.id ? null : role.id)}
                      onClick={() => setExpandedRoleId(expandedRoleId === role.id ? null : role.id)}
                      sx={{ bgcolor: "#f3f0ff", color: "#6941c6", border: "1px solid #d9d0ff", cursor: "pointer", "& .MuiChip-icon": { color: "#6941c6" }, "& .MuiChip-deleteIcon": { color: "#6941c6" } }}
                    />
                  </Box>
                  <Collapse in={expandedRoleId === role.id} timeout="auto">
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1 }}>
                      {Object.entries(
                        role.permissions.reduce<Record<string, PermissionItem[]>>((acc, permName) => {
                          const perm = allPermissions.find((x) => x.name === permName);
                          if (perm) {
                            if (!acc[perm.permissionGroup]) acc[perm.permissionGroup] = [];
                            acc[perm.permissionGroup].push(perm);
                          }
                          return acc;
                        }, {})
                      ).map(([group, perms]) => (
                        <Box key={group} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
                          <Typography fontSize={12} fontWeight={600} color="text.secondary" sx={{ minWidth: 160, flexShrink: 0 }}>{group}</Typography>
                          {perms.map((p) => (
                            <Chip
                              key={p.id}
                              label={p.displayName}
                              size="small"
                              sx={{ bgcolor: "#f3f0ff", color: "#6941c6", border: "1px solid #d9d0ff", fontSize: 11 }}
                            />
                          ))}
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
                {havePermission(Permissions.Role_Update) && role.id !== 1 && (
                  <IconButton size="small" onClick={() => openEdit(role)}>
                    <Edit fontSize="small" />
                  </IconButton>
                )}
                {havePermission(Permissions.Role_Delete) && role.id !== 1 && (
                  <IconButton size="small" color="error" onClick={() => handleDelete(role.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
            {filtered.length === 0 && (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary">Kayıt bulunamadı.</Typography>
              </Box>
            )}
          </Box>
        )}
      </div>

      <Dialog open={dialogOpen} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Typography fontWeight={600} fontSize={18}>
            {editId !== null ? "Rol Düzenle" : "Rol Ekle"}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            size="small"
            label="Rol Adı"
            value={roleName}
            onChange={(e) => { setRoleName(e.target.value); if (e.target.value.trim()) setRoleNameError(""); }}
            fullWidth
            error={!!roleNameError}
            helperText={roleNameError}
            sx={{ mb: 3 }}
          />

          <Typography fontWeight={600} fontSize={14} mb={1}>Yetkiler</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {Object.entries(groupedPermissions).map(([group, perms]) => {
              const groupIds = perms.map((p) => p.id);
              const allChecked = groupIds.every((id) => selectedIds.has(id));
              const someChecked = groupIds.some((id) => selectedIds.has(id)) && !allChecked;
              return (
                <Box key={group} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, px: 2, pt: 0.5, pb: 1 }}>
                  <FormControlLabel
                    label={<Typography fontWeight={600} fontSize={13}>{group}</Typography>}
                    control={
                      <Checkbox
                        size="small"
                        checked={allChecked}
                        indeterminate={someChecked}
                        onChange={() => toggleGroup(group)}
                      />
                    }
                  />
                  <Divider sx={{ mb: 0.75 }} />
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {perms.map((p) => (
                      <FormControlLabel
                        key={p.id}
                        label={<Typography fontSize={13}>{p.displayName}</Typography>}
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedIds.has(p.id)}
                            onChange={() => togglePermission(p.id)}
                          />
                        }
                        sx={{ mr: 1 }}
                      />
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{ textTransform: "none", borderRadius: "6px", borderColor: "var(--color-primary)", color: "var(--color-primary)" }}
            >
              İptal
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{ textTransform: "none", borderRadius: "6px", bgcolor: "var(--color-primary)", "&:hover": { bgcolor: "var(--color-primary-dark)" } }}
            >
              Kaydet
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <CustomSnackbar {...snackbarProps} />
    </>
  );
};

export default Roles;
